import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { authApi, AuthResponse } from 'app/apis/authApi';
import { RootState } from 'app/store';
import {
  isSandboxUser,
  isSuperUser,
  getUserDetails
} from 'common/utils/jwt-helpers';
import { PURGE } from 'redux-persist';

interface AuthState {
  accessToken: string | null;
  scope: string | null;
  expiresIn: number | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  idToken: string | null;
}

const LoggedOutState: AuthState = {
  accessToken: null,
  scope: null,
  expiresIn: null,
  refreshToken: null,
  isAuthenticated: false,
  idToken: null
};

const initialState = LoggedOutState;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => {
      return LoggedOutState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, (state) => {
        return LoggedOutState;
      })
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload: action }: PayloadAction<AuthResponse>) => {
          state.accessToken = action.access_token;
          state.scope = action.scope;
          state.expiresIn = action.expires_in;
          state.refreshToken = action.refresh_token;
          state.isAuthenticated = true;
        }
      )
      .addMatcher(authApi.endpoints.login.matchPending, (state, action) => {
        console.log('pending', action);
      })
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        console.log('rejected', action);
      });

    builder.addMatcher(
      authApi.endpoints.authenticate.matchFulfilled,
      (state, action: PayloadAction<string>) => {
        state.idToken = action.payload;
      }
    );
  }
});

const { actions, reducer } = authSlice;

export const { logout } = actions;

export default reducer;

export const selectSelf = (state: RootState) => state.auth;

export const selectIdToken = createSelector(
  selectSelf,
  (state) => state.idToken
);

export const selectIsSuperUser = createSelector(selectSelf, (state) => {
  if (state.idToken == null) {
    return false;
  }

  return isSuperUser(state.idToken);
});

export const selectIsSandboxUser = createSelector(selectSelf, (state) => {
  if (state.idToken == null) {
    return false;
  }

  return isSandboxUser(state.idToken);
});

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;

export const selectUserDetails = createSelector(selectSelf, (state) => {
  if (state.idToken == null) {
    return {
      FirstName: '',
      LastName: '',
      Email: '',
      UserName: ''
    };
  }

  return getUserDetails(state.idToken);
});
